import { ComponentDataDestService } from "../component-data-dest.service";
import { ComponentDataSourceService } from "../../component-data-source/component-data-source.service";
import { FunctionRegistryService } from '../../function-registry/function-registry.service';
import { catchError, map } from 'rxjs';
import { environment } from '../../../environment/environment';
import { EducationalPathRequirementData, PathItemRequirement } from '../../../interfaces/educational-path.interfaces';

export function put_educational_path_requirement(
    componentDataDestService: ComponentDataDestService, 
    componentDataSourceService: ComponentDataSourceService, 
    data: Partial<EducationalPathRequirementData>
) {
    const LOG_PREFIX = '[Educational Path Requirement]';
    
    // Ensure all arrays exist with default empty arrays
    const safeData = {
        ...data,
        subtypes: data.dualPanelSubtypes?.dualPanelSubtypes || [],
        modules: data.dualPanelModules?.dualPanelModules || []
    };

    // Validate required fields
    if (!safeData.version_start_date) {
        throw new Error('Start date is required');
    }
    if (!safeData.uuid) {
        throw new Error('Educational path UUID is required');
    }
    if (!safeData.version_id) {
        throw new Error('Version ID is required');
    }

    // Convert the selections into PathItem format
    const requirements: PathItemRequirement[] = [
        ...safeData.subtypes.map(id => ({
            item_type: 'Subtype' as const,
            item_id: id,
            version_start_date: safeData.version_start_date!,
            version_end_date: safeData.version_end_date,
            visibility: safeData.visibility
        })),
        ...safeData.modules.map(id => ({
            item_type: 'Module' as const,
            item_id: id,
            version_start_date: safeData.version_start_date!,
            version_end_date: safeData.version_end_date,
            visibility: safeData.visibility
        }))
    ];

    // Validate that at least one requirement is selected
    if (requirements.length === 0) {
        throw new Error('At least one requirement (subtype or module) must be selected');
    }

    // Validate dates
    const startDate = new Date(safeData.version_start_date);
    if (isNaN(startDate.getTime())) {
        throw new Error('Invalid start date format');
    }
    if (safeData.version_end_date) {
        const endDate = new Date(safeData.version_end_date);
        if (isNaN(endDate.getTime())) {
            throw new Error('Invalid end date format');
        }
        if (endDate < startDate) {
            throw new Error('End date cannot be before start date');
        }
    }

    const requestData = {
        requirements,
        visibility: safeData.visibility
    };

    // Send the requirements to the API
    const request$ = componentDataDestService.http.post(
        `${environment.apiUrl}/paths/versions/${safeData.version_id}/requirements`,
        requestData,
        { withCredentials: true }
    ).pipe(
        catchError(error => {
            console.error(`${LOG_PREFIX} API Error:`, error);
            throw error;
        }),
        map(response => {
            return response;
        })
    );

    // Update the store and refresh modules list on success
    componentDataSourceService.updateStoreData(request$, 'educational_path_requirements').subscribe({
        next: (response) => {
            try {
                componentDataDestService.injector.get(FunctionRegistryService).executeFunction('refreshEducationalPathModules');
            } catch (e) {
                // Modules component may not be on page
            }
        },
        error: (error) => console.error(`${LOG_PREFIX} Store update error:`, error)
    });
} 